import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { RoutingTargetDisplayNamesResponse } from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

type DisplayScope = { readonly configId: string } | 'default';

export const ROUTING_TARGET_DISPLAY_NAMES_KEY = ['tenant-routing', 'target-display-names'] as const;

export function routingTargetDisplayNamesKey(scope: DisplayScope | null): readonly string[] {
  return [...ROUTING_TARGET_DISPLAY_NAMES_KEY,
    scope === 'default' ? 'default' : 'config',
    scope === null || scope === 'default' ? '' : scope.configId];
}

export function getRoutingTargetDisplayNames(
  scope: DisplayScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RoutingTargetDisplayNamesResponse> {
  const path = scope === 'default'
    ? '/default-target-display-names'
    : `/configs/${encodeURIComponent(scope.configId)}/target-display-names`;
  return opsApiRequest(`/v1/ops/tenant-routing${path}`, { signal }, fetchImpl);
}

/** The sheet owns this query, so changing SKU or routing mode never fans it out. */
export function useRoutingTargetDisplayNames(
  scope: DisplayScope | null,
): UseQueryResult<RoutingTargetDisplayNamesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: routingTargetDisplayNamesKey(scope),
    queryFn: ({ signal }) => {
      if (scope === null) throw new Error('target display scope is required');
      return getRoutingTargetDisplayNames(scope, fetchImpl, signal);
    },
    enabled: scope !== null,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function invalidateRoutingTargetDisplayNames(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ROUTING_TARGET_DISPLAY_NAMES_KEY });
}
